/**
 * Referrals — saved link manager.
 *
 * The operator builds a library of named tracked links (one per partner,
 * IG bio, TG channel, etc.). Each row shows the live URL ready to copy,
 * 30-day + lifetime click counts, plus edit/delete actions. New links
 * created inline at the top of the table.
 *
 * URLs are always generated against window.location.origin so they reflect
 * the brand domain (2highexotics.com) and never leak the upstream host.
 */
import React from "react";
import { api } from "../../lib/admin/api";

const PUBLIC_ORIGIN =
  (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/+$/, "") ||
  (typeof window !== "undefined" && window.location?.origin) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

const LANDING_OPTIONS = [
  { value: "/", label: "Homepage" },
  { value: "/menu", label: "Live Menu" },
  { value: "/about", label: "About / Story" },
  { value: "/contact", label: "Contact" },
  { value: "/drops", label: "Drops" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function buildUrl(slug, landing) {
  const path = landing && landing !== "/" ? `/r/${slug}?to=${encodeURIComponent(landing)}` : `/r/${slug}`;
  return `${PUBLIC_ORIGIN}${path}`;
}

export default function Referrals() {
  const [links, setLinks] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copiedId, setCopiedId] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [days, setDays] = React.useState(30);

  // Create-form state
  const [draft, setDraft] = React.useState({ name: "", slug: "", landing: "/menu", notes: "" });
  const [creating, setCreating] = React.useState(false);
  const [createErr, setCreateErr] = React.useState("");

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.listReferralLinks(), api.referralStats(days)]);
      setLinks(l);
      setStats(s);
    } catch (e) {
      console.error("[Referrals] load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [days]);

  React.useEffect(() => { reload(); }, [reload]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1600);
    });
  };

  const onCreate = async (e) => {
    e?.preventDefault?.();
    setCreateErr("");
    const slug = (draft.slug || draft.name).toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 48);
    if (!slug) { setCreateErr("Slug or partner name required"); return; }
    setCreating(true);
    try {
      await api.createReferralLink({
        name: draft.name || slug,
        slug,
        landing: draft.landing,
        notes: draft.notes,
      });
      setDraft({ name: "", slug: "", landing: "/menu", notes: "" });
      await reload();
    } catch (err) {
      setCreateErr(err.response?.data?.detail || "Could not create link");
    } finally {
      setCreating(false);
    }
  };

  const onSaveEdit = async (id, patch) => {
    try {
      await api.updateReferralLink(id, patch);
      setEditingId(null);
      await reload();
    } catch (err) {
      alert(err.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (link) => {
    if (!window.confirm(`Delete the referral link "${link.name || link.slug}"?\n\nClick history will be preserved for analytics.`)) return;
    try {
      await api.deleteReferralLink(link.id);
      await reload();
    } catch (err) {
      alert(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="admin-referrals" className="p-6 max-w-[1200px] space-y-8">
      <header className="space-y-2">
        <h1 className="text-[22px] font-bold text-[#f3eed8] tracking-[.04em]">Referral Links</h1>
        <p className="text-[12.5px] text-[#a9aa9a] leading-relaxed max-w-2xl">
          Build a library of tracked links — one per partner, IG bio, Telegram
          channel, etc. Every click is logged. Edit any link anytime; the URL
          updates instantly. Send just the URL to whoever needs it — no HTML required.
        </p>
      </header>

      {/* Stats summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label={`Clicks · ${days}d`} value={stats.total_clicks} highlight />
          <StatCard label="Clicks all-time" value={stats.total_clicks_all_time} />
          <StatCard label="Active links" value={links.length} />
          <div className="rounded-2xl border border-[#22261b] bg-[#13150f] p-4 flex items-center gap-2">
            {[7, 30, 90, 365].map(n => (
              <button
                key={n}
                onClick={() => setDays(n)}
                data-testid={`window-${n}`}
                className={`flex-1 py-1.5 text-[10px] tracking-[.18em] uppercase border rounded-md ${
                  days === n
                    ? "bg-[#c9a86b] text-[#1a1407] border-[#c9a86b]"
                    : "text-[#c9a86b] border-[#22261b] hover:border-[#c9a86b]/40"
                }`}
              >{n === 365 ? "1Y" : `${n}D`}</button>
            ))}
          </div>
        </div>
      )}

      {/* Create form */}
      <form
        data-testid="referral-create-form"
        onSubmit={onCreate}
        className="rounded-2xl border border-[#c9a86b]/30 bg-[#13150f] p-5 space-y-3"
      >
        <h2 className="text-[12.5px] tracking-[.16em] uppercase text-[#c9a86b] font-bold">
          + Create new link
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Partner / Channel name">
            <Input
              testid="new-link-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value, slug: draft.slug || e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              placeholder="Choke Promos"
            />
          </Field>
          <Field label="URL slug" hint={`Becomes /r/${draft.slug || "<slug>"}`}>
            <Input
              testid="new-link-slug"
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
              placeholder="choke"
            />
          </Field>
          <Field label="Landing page">
            <Select
              testid="new-link-landing"
              value={draft.landing}
              onChange={(e) => setDraft({ ...draft, landing: e.target.value })}
              options={LANDING_OPTIONS}
            />
          </Field>
          <Field label="Notes (optional)">
            <Input
              testid="new-link-notes"
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="e.g. button on /2high/ page"
            />
          </Field>
        </div>
        {createErr && <div className="text-[11px] text-red-400">{createErr}</div>}
        <button
          type="submit"
          data-testid="new-link-submit"
          disabled={creating}
          className="px-5 py-2.5 rounded-md text-[11px] tracking-[.18em] uppercase font-bold bg-[#c9a86b] text-[#1a1407] hover:bg-[#d4b675] disabled:opacity-50"
        >{creating ? "Creating…" : "Create link"}</button>
      </form>

      {/* Saved links list */}
      <div className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#22261b] text-[10.5px] tracking-[.16em] uppercase text-[#a9aa9a]">
          Saved Links {links.length > 0 && `· ${links.length}`}
        </div>
        {loading && <div className="p-6 text-[12px] text-[#6e6f5f]">Loading…</div>}
        {!loading && links.length === 0 && (
          <div className="p-8 text-center text-[12px] text-[#6e6f5f]">
            No links yet. Create your first one above 👆
          </div>
        )}
        {!loading && links.map((link) => (
          <LinkRow
            key={link.id}
            link={link}
            isEditing={editingId === link.id}
            onEdit={() => setEditingId(link.id)}
            onCancelEdit={() => setEditingId(null)}
            onSave={(patch) => onSaveEdit(link.id, patch)}
            onDelete={() => onDelete(link)}
            onCopy={(url) => copy(url, link.id)}
            copied={copiedId === link.id}
          />
        ))}
      </div>
    </section>
  );
}

/* =========================== sub-components =========================== */

function LinkRow({ link, isEditing, onEdit, onCancelEdit, onSave, onDelete, onCopy, copied }) {
  const [form, setForm] = React.useState({
    name: link.name || link.slug,
    slug: link.slug,
    landing: link.landing,
    notes: link.notes || "",
  });

  React.useEffect(() => {
    setForm({ name: link.name || link.slug, slug: link.slug, landing: link.landing, notes: link.notes || "" });
  }, [link, isEditing]);

  const url = buildUrl(isEditing ? form.slug : link.slug, isEditing ? form.landing : link.landing);

  return (
    <div data-testid={`link-row-${link.id}`} className="px-4 py-4 border-b border-[#22261b] last:border-b-0">
      {!isEditing ? (
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_auto_auto_auto_auto] gap-3 items-center">
          {/* Name + URL */}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[13.5px] font-bold text-[#f3eed8]">{link.name || link.slug}</span>
              <span className="text-[10px] text-[#6e6f5f] uppercase tracking-wider">→ {LANDING_OPTIONS.find(o => o.value === link.landing)?.label || link.landing}</span>
            </div>
            <div className="text-[11px] text-[#c9a86b] font-mono mt-1 break-all">{url}</div>
            {link.notes && <div className="text-[10.5px] text-[#6e6f5f] mt-1">{link.notes}</div>}
          </div>

          {/* Click stats */}
          <div className="text-center min-w-[70px]">
            <div className="text-[9px] tracking-[.16em] uppercase text-[#6e6f5f]">30D</div>
            <div className="text-[18px] font-bold text-[#c9a86b] tabular-nums">{link.clicks_30d}</div>
          </div>
          <div className="text-center min-w-[70px]">
            <div className="text-[9px] tracking-[.16em] uppercase text-[#6e6f5f]">All-time</div>
            <div className="text-[18px] font-bold text-[#f3eed8] tabular-nums">{link.clicks_all_time}</div>
          </div>

          {/* Actions */}
          <button
            data-testid={`copy-${link.id}`}
            onClick={() => onCopy(url)}
            className="px-3 py-2 text-[10px] tracking-[.18em] uppercase border border-[#c9a86b]/40 text-[#c9a86b] rounded-md hover:bg-[#c9a86b]/[.08] whitespace-nowrap"
          >{copied ? "✓ Copied" : "Copy URL"}</button>
          <div className="flex gap-2">
            <button
              data-testid={`edit-${link.id}`}
              onClick={onEdit}
              className="px-3 py-2 text-[10px] tracking-[.18em] uppercase border border-[#22261b] text-[#a9aa9a] rounded-md hover:border-[#c9a86b]/40 hover:text-[#c9a86b]"
            >Edit</button>
            <button
              data-testid={`delete-${link.id}`}
              onClick={onDelete}
              className="px-3 py-2 text-[10px] tracking-[.18em] uppercase border border-red-500/30 text-red-300 rounded-md hover:bg-red-500/10"
            >Del</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Name">
            <Input testid={`edit-name-${link.id}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Slug" hint={`Becomes /r/${form.slug}`}>
            <Input testid={`edit-slug-${link.id}`} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} />
          </Field>
          <Field label="Landing">
            <Select testid={`edit-landing-${link.id}`} value={form.landing} onChange={(e) => setForm({ ...form, landing: e.target.value })} options={LANDING_OPTIONS} />
          </Field>
          <Field label="Notes">
            <Input testid={`edit-notes-${link.id}`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="md:col-span-4 flex items-center gap-2 pt-1">
            <button
              data-testid={`save-${link.id}`}
              onClick={() => onSave(form)}
              className="px-4 py-2 rounded-md text-[10.5px] tracking-[.18em] uppercase font-bold bg-[#c9a86b] text-[#1a1407] hover:bg-[#d4b675]"
            >Save</button>
            <button
              data-testid={`cancel-${link.id}`}
              onClick={onCancelEdit}
              className="px-4 py-2 rounded-md text-[10.5px] tracking-[.18em] uppercase border border-[#22261b] text-[#a9aa9a] hover:border-[#c9a86b]/40"
            >Cancel</button>
            {form.slug !== link.slug && (
              <span className="text-[10.5px] text-yellow-400/80 ml-2">
                ⚠ Changing the slug breaks any link already in use with the old URL.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-[#c9a86b]/30 bg-[#c9a86b]/[.04]" : "border-[#22261b] bg-[#13150f]"}`}>
      <div className="text-[10px] tracking-[.18em] uppercase text-[#6e6f5f] mb-2">{label}</div>
      <div className={`text-[26px] font-bold tabular-nums ${highlight ? "text-[#c9a86b]" : "text-[#f3eed8]"}`}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[.16em] uppercase text-[#6e6f5f] mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-[#6e6f5f] mt-1">{hint}</span>}
    </label>
  );
}

function Input({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full px-3 py-2 rounded-md bg-black/30 border border-[#22261b] text-[#f3eed8] text-sm focus:outline-none focus:border-[#c9a86b]/60"
    />
  );
}

function Select({ testid, options, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className="w-full px-3 py-2 rounded-md bg-black/30 border border-[#22261b] text-[#f3eed8] text-sm focus:outline-none focus:border-[#c9a86b]/60"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
